import {
    computed,
    type ComputedRef,
    type MaybeRef,
    onMounted,
    type Ref,
    ref,
    type ShallowRef,
    unref,
    useTemplateRef,
    watch,
    type WritableComputedRef,
} from 'vue'
import { useEventListener, useWindowSize } from '@vueuse/core'
import { getRequestAnimationFrame } from '@/helpers/globals'

export type AnimationFrameFunction = (
    item: HTMLElement, // apply transforms to this element
    itemParent: HTMLElement, // use to get item measurments (width, position)
    container: HTMLElement // scroll container
) => void

// ensure correct SSR state
export const CARAOUSEL_INITIAL_SLIDE_WIDTH = 20.2

interface Carousel {
    container: Readonly<ShallowRef<HTMLDivElement | null>>
    elements: Readonly<ShallowRef<HTMLDivElement[] | null>>
    itemsAnimated: Ref<HTMLElement[]>
    current: WritableComputedRef<number, number>
    scrollPercent: ComputedRef<number>
    isAtStart: ComputedRef<boolean>
    isAtEnd: ComputedRef<boolean>
    previous: () => void
    next: () => void
    slideWidth: Ref<number>
    containerScrollLeft: Ref<number>
    containerOffsetWidth: Ref<number>
}

type Options =
    | {
          isCentered?: MaybeRef<boolean>
          animation?: AnimationFrameFunction
          behavior?: 'smooth' | 'instant'
          initial?: number
          hasVideo?: boolean
          hasSeeInStoreCta?: boolean
          // make sure it runs after any other vue code in the thread
          // this is done to prevent calling element measurement APIs mid-thread
          // https://github.com/MicrosoftEdge/MSEdgeExplainers/blob/main/EventPhases/explainer.md#how-does-this-happen
          // should be disabled for modals, otherwise will render the first slide before
          // adjusting to current slide
          forcedReflowOptimization?: boolean
      }
    | undefined

export function useCarousel(
    items: Ref<unknown[]>, // used to calculate correct SSR state
    {
        isCentered = false,
        animation,
        behavior = 'smooth',
        initial,
        hasVideo,
        hasSeeInStoreCta,
        forcedReflowOptimization = true,
    }: Options = {
        isCentered: false,
        animation: undefined,
        behavior: 'smooth',
        initial: undefined,
        hasVideo: false,
        hasSeeInStoreCta: false,
        forcedReflowOptimization: true,
    }
): Carousel {
    const container = useTemplateRef<HTMLDivElement>('container')
    const elements = useTemplateRef<HTMLDivElement[]>('elements')
    const slideWidth = ref(CARAOUSEL_INITIAL_SLIDE_WIDTH)
    const containerScrollLeft = ref(0)

    useEventListener(
        container,
        'scroll',
        (e) => {
            containerScrollLeft.value = (e.target as HTMLElement).scrollLeft
        },
        { passive: true }
    )

    const scrollTo = (x: number) => {
        container.value?.scrollTo({
            left: x,
            behavior,
        })
    }
    const containerOffsetWidth = ref(CARAOUSEL_INITIAL_SLIDE_WIDTH)
    const updateMeasurments = () => {
        if (elements.value?.length && container.value) {
            slideWidth.value = Math.floor(
                elements.value[0]?.getBoundingClientRect().width || 0
            )

            containerOffsetWidth.value = Math.floor(
                container.value.getBoundingClientRect().width
            )
        }
    }

    function calculateContainerScrollLeft(
        index: number,
        isCentered: boolean
    ): number {
        if (isCentered) {
            return (
                index * slideWidth.value -
                containerOffsetWidth.value / 2 +
                slideWidth.value / 2
            )
        }

        return index * slideWidth.value
    }

    function calculateCurrent(isCentered: boolean): number {
        if (isCentered) {
            return Math.round(
                (containerScrollLeft.value +
                    containerOffsetWidth.value / 2 -
                    slideWidth.value / 2) /
                    slideWidth.value
            )
        }

        return Math.round(containerScrollLeft.value / slideWidth.value)
    }

    const isMounted = ref(false)

    const init = () => {
        updateMeasurments()

        if (typeof initial !== 'undefined') {
            container.value?.scrollTo({
                left: calculateContainerScrollLeft(initial, unref(isCentered)),
                behavior: 'instant',
            })

            isMounted.value = true
        }
    }

    // client-only
    onMounted(() => {
        if (forcedReflowOptimization) {
            // eslint-disable-next-line no-restricted-globals
            setTimeout(init)
        } else {
            init()
        }
    })

    if (!import.meta.env.SSR) {
        const { width } = useWindowSize()

        watch(width, updateMeasurments)
    }

    const current = computed({
        get() {
            if (typeof initial !== 'undefined' && !isMounted.value) {
                // This prevents current from being 0, 1, 2, 3, etc. when initializing
                // Without this, carousel dots will flicker through to the initial image
                return initial
            }

            return calculateCurrent(unref(isCentered)) || 0
        },
        set(index: number) {
            scrollTo(calculateContainerScrollLeft(index, unref(isCentered)))
        },
    })

    const isAtStart = computed(() => current.value <= 0)
    const isAtEnd = computed(
        () =>
            current.value >=
            items.value.length +
                (hasSeeInStoreCta ? 1 : 0) +
                (hasVideo ? 1 : 0) -
                Math.floor(containerOffsetWidth.value / slideWidth.value)
    )

    const previous = () => {
        current.value -= 1
    }

    const next = () => {
        current.value += 1
    }

    const scrollPercent = computed(
        // in case of division by 0, return 0 instead of NaN
        () => containerScrollLeft.value / containerOffsetWidth.value || 0
    )

    const requestAnimationFrame = getRequestAnimationFrame()
    const itemsAnimated = ref<HTMLElement[]>([]) as Ref<HTMLElement[]>
    const animate = () => {
        if (!container.value || !animation || !elements.value) {
            return
        }

        for (let i = 0; i < elements.value.length; i += 1) {
            const animetedElement = itemsAnimated.value[i]
            const element = elements.value[i]

            if (!animetedElement || !element) {
                return
            }

            animation(animetedElement, element, container.value)
        }

        requestAnimationFrame(animate)
    }

    if (animation && !import.meta.env.SSR) {
        requestAnimationFrame(animate)
    }

    return {
        container,
        elements,
        itemsAnimated,
        current,
        scrollPercent,
        isAtStart,
        isAtEnd,
        previous,
        next,
        slideWidth,
        containerScrollLeft,
        containerOffsetWidth,
    }
}
